import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useRead from "./useRead";
import usePut from "./usePut";
import Cookies from "universal-cookie";
import { COMPANY_EMPLOYEE_ID } from "../config";
import toast from "react-hot-toast";

const cookies = new Cookies();

export const useAttendanceApi = (initialMonth = new Date()) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get("month_and_year");
  const defaultMonth = monthParam ? new Date(monthParam) : initialMonth;

  const [filterData, setFilterData] = useState({
    month_and_year: defaultMonth,
    company_employee_id: cookies.get(COMPANY_EMPLOYEE_ID),
  });

  const [isRegularizationModalOpen, setIsRegularizationModalOpen] = useState(false);
  const [selectedAttendanceForRegularization, setSelectedAttendanceForRegularization] = useState(null);
  const [activeTab, setActiveTab] = useState("request");

  // Fetch attendance list
  const { list: attendanceData, setGetListParams: setAttendanceParams } = useRead({
    url: "singleuserattendancelist",
    initialData: filterData,
    method: "POST",
  });

  // Submit regularization request
  const submitRegularization = usePut({
    url: "attendance-regularization/request",
    refreshUrl: "singleuserattendancelist",
    onSuccess: (response) => {
      toast.success("Regularization request submitted successfully");
      
      // Switch to history tab automatically
      // The history tab will fetch fresh data and show the pending request
      setActiveTab("history");
      
      // Refresh attendance list after successful submission
      setAttendanceParams(filterData);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to submit regularization request"
      );
    },
  });

  const handleMonthChange = (newDate) => {
    const newFilterData = {
      ...filterData,
      month_and_year: newDate,
    };
    setFilterData(newFilterData);
    setAttendanceParams(newFilterData);
    
    const params = Object.fromEntries(searchParams.entries());
    if (newDate) {
      setSearchParams({ ...params, month_and_year: newDate.toISOString() });
    } else {
      delete params.month_and_year;
      setSearchParams(params);
    }
  };

  const handleSubmitRegularization = (payload) => {
    submitRegularization.handleAuth.mutate(payload);
  };

  const handleOpenRegularizationModal = (attendanceData) => {
    setSelectedAttendanceForRegularization(attendanceData);
    setIsRegularizationModalOpen(true);
    
    // Set the tab based on whether there's a pending request
    if (attendanceData?.pending_regularization_request) {
      setActiveTab("history");
    } else {
      setActiveTab("request");
    }
  };

  const handleCloseRegularizationModal = () => {
    setIsRegularizationModalOpen(false);
    setSelectedAttendanceForRegularization(null);
    setActiveTab("request");
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  return {
    // Data
    attendanceList: attendanceData?.data || [],
    isLoading: attendanceData?.isLoading,
    filterData,
    
    // Modal states
    isRegularizationModalOpen,
    selectedAttendanceForRegularization,
    handleOpenRegularizationModal,
    handleCloseRegularizationModal,
    
    // Tab state
    activeTab,
    handleTabChange,
    
    // Handlers
    handleMonthChange,
    handleSubmitRegularization,
    
    // Loading states
    isSubmitting: submitRegularization.isLoading,
  };
};
